import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { WebService } from '../../../services/web/web.service';

export interface PortalPageRequest {
  pageIndex: number;
  pageSize: number;
  search?: string;
  active?: boolean; // undefined = todos
}

export interface PortalPage {
  portals: any[];
  totalEntities: number;
  pages: number;
  pageIndex: number;
  pageSize: number;
  serverPaged: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class PortalService {

  // BehaviorSubject (no EventEmitter) para que suscriptores tardíos reciban
  // el último valor emitido. Crítico cuando un componente se monta DESPUÉS
  // de que la lista de portales se haya cargado en otro lado.
  public portals = new BehaviorSubject<any[]>([]);

  // true si el partner tiene al menos un portal SIN filtros aplicados.
  // Solo lo actualizan las cargas sin search/active.
  public hasPortals = new BehaviorSubject<boolean>(false);

  constructor(private webService: WebService) { }

  getPortalsPaged(partnerId: number, req: PortalPageRequest): Observable<PortalPage> {
    let url = `${this.webService.HOST}/onboarging/partner/${partnerId}?pageIndex=${req.pageIndex}&pageSize=${req.pageSize}`;
    if (req.search) {
      url += `&search=${encodeURIComponent(req.search)}`;
    }
    if (req.active !== undefined) {
      url += `&active=${req.active ? 1 : 0}`;
    }

    const unfiltered = !req.search && req.active === undefined;

    return this.webService.get(url).pipe(
      map(response => {
        const page = this.toPortalPage(response, req);
        if (unfiltered) {
          this.hasPortals.next(page.totalEntities > 0);
        }
        return page;
      })
    );
  }

  private toPortalPage(response: any, req: PortalPageRequest): PortalPage {
    const result = response?.result;

    // Shape nuevo del backend: ListPager {entities, pager}
    if (result && !Array.isArray(result) && Array.isArray(result.entities) && result.pager) {
      return {
        portals: result.entities.map((p: any) => this.processPortal(p)),
        totalEntities: result.pager.totalEntities ?? result.entities.length,
        pages: result.pager.pages ?? 1,
        pageIndex: result.pager.pageIndex ?? req.pageIndex,
        pageSize: result.pager.pageSize ?? req.pageSize,
        serverPaged: true
      };
    }

    // Shape actual (backend viejo): array plano → paginar/filtrar en memoria
    const all: any[] = Array.isArray(result) ? result : [];
    all.sort((a: { id: number; }, b: { id: number; }) => b.id - a.id);

    const filtered = all.filter(p => this.matchesFilters(p, req));
    const pages = Math.max(1, Math.ceil(filtered.length / req.pageSize));
    const start = req.pageIndex * req.pageSize;

    return {
      portals: filtered.slice(start, start + req.pageSize).map(p => this.processPortal(p)),
      totalEntities: filtered.length,
      pages,
      pageIndex: req.pageIndex,
      pageSize: req.pageSize,
      serverPaged: false
    };
  }

  private matchesFilters(portal: any, req: PortalPageRequest): boolean {
    if (req.active !== undefined) {
      const isActive = portal.isActive === true;
      if (isActive !== req.active) {
        return false;
      }
    }
    if (req.search) {
      const term = req.search.toLowerCase();
      const haystack = [
        portal.service?.name_service,
        portal.domain_portal,
        portal.url_client
      ].filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(term);
    }
    return true;
  }

  // Mismo procesamiento que hacía MyPortalsComponent: url de preview del template.
  private processPortal(portal: any): any {
    const service = portal.service?.id != null ? portal.service.id : portal.service;
    return { ...portal, url_template: portal.url_template + '/preview/' + service };
  }
}
